import { useNavigate } from 'react-router-dom';
import React, { useEffect, useMemo, useRef, useState } from 'react';

import { debounce } from 'lodash';
import { useQueryClient } from '@tanstack/react-query';

import { TagContextProvider } from 'lib/context/TagContext/TagContextProvider';

import ErrorBoundary from 'components/shared/Error/Boundary';

import TagTableView from './components/TagTableView';
import { updateTagLinks } from './lib/updateTagLinks';
import TagsReorder from './components/TagsReorder/TagsReorder';

/**
 * @param {import('./lib/propTypes').TagsProps} props
 */

const Tags = (props) => {
  const {
    value,
    data,
    query,
    refTable,
    kind,
    label,
    fromTable = false,
    readOnly = false,
    tableActions: customTableActions,
    manageTagsPath = '/portal/settings/tags',
    onOptimisticUpdate,
    popoverPosition = 'left',
    popoverPanelClassName,
    showTags = true,
    triggerLabel
  } = props;
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [isCreateModal, setIsCreateModal] = useState(false);
  const [openReorderModal, setOpenReorderModal] = useState(false);
  // Tag ids with a save in flight — the dropdown shows a loader on these and
  // ignores further clicks on them until the write lands.
  const [savingTagIds, setSavingTagIds] = useState([]);

  // Assigned in an effect (not during render) so the ref only ever holds committed values —
  // a concurrent render that React discards must not leak its params into the debounced call.
  const updateParamsRef = useRef();
  useEffect(() => {
    updateParamsRef.current = { navigate, queryClient, query, refTable, fromTable };
  });

  // Selections made inside the debounce window accumulate per row. Passing the
  // tags as debounce arguments instead would let a later click replace an
  // earlier one's payload, so a quickly-picked tag is drawn as selected by the
  // optimistic update but never actually saved.
  const pendingAddsRef = useRef(new Map());

  const flushPendingAdds = useMemo(
    () =>
      debounce(async () => {
        const pending = pendingAddsRef.current;
        pendingAddsRef.current = new Map();
        if (!pending.size) return;

        // Only this batch's ids may be cleared: a selection made while the batch
        // was in flight, or a remove running alongside it, is still unsaved and
        // has to keep its loader.
        const flushedIds = new Set([...pending.values()].flat().map((tag) => tag?.id));

        try {
          await Promise.all(
            [...pending].map(([id, tags]) =>
              updateTagLinks({ id, tags, mode: 'add', ...updateParamsRef.current })
            )
          );
        } finally {
          setSavingTagIds((prev) => prev.filter((tagId) => !flushedIds.has(tagId)));
        }
      }, 1000),
    []
  );

  useEffect(() => () => flushPendingAdds.flush(), [flushPendingAdds]);

  const handleAddTags = (data, tag) => {
    const filteredNewTags = tag?.filter(
      (newTag) => !data?.tagLinks?.some((existingTag) => existingTag?.tag_id === newTag?.id)
    );
    if (!filteredNewTags?.length) return;
    onOptimisticUpdate?.(data, filteredNewTags, 'add');

    const id = data?.id;
    const queued = pendingAddsRef.current.get(id) || [];
    const merged = [...queued];
    filteredNewTags.forEach((newTag) => {
      if (!merged.some((queuedTag) => queuedTag?.id === newTag?.id)) merged.push(newTag);
    });
    pendingAddsRef.current.set(id, merged);
    setSavingTagIds((prev) => [
      ...prev,
      ...filteredNewTags.map((newTag) => newTag?.id).filter((tagId) => !prev.includes(tagId))
    ]);
    flushPendingAdds();
  };

  const handleRemoveTag = async (data, id) => {
    onOptimisticUpdate?.(data, [id], 'remove');
    setSavingTagIds((prev) => (prev.includes(id) ? prev : [...prev, id]));
    try {
      await updateTagLinks({
        navigate,
        queryClient,
        id: data?.id,
        fromTable,
        tags: [id],
        mode: 'remove',
        query,
        refTable
      });
    } finally {
      setSavingTagIds((prev) => prev.filter((tagId) => tagId !== id));
    }
  };

  const defaultTableActions = readOnly
    ? []
    : [
        {
          label: 'Create tag',
          icon: 'tag-new-mono',
          onClick: () => setIsCreateModal(true)
        },
        {
          label: 'Reorder tags',
          icon: 'arrow-swap-down-mono',
          onClick: () => setOpenReorderModal(true),
          disabled: value?.length < 2,
          tippyText:
            value?.length < 2
              ? `Select tags to add to this ${label || kind}`
              : 'Reorder selected tags'
        },
        {
          label: 'Manage tags',
          icon: 'settings-mono',
          onClick: () => navigate(manageTagsPath),
          disabled: false
        }
      ];

  const tableActions = customTableActions ?? defaultTableActions;

  return (
    <ErrorBoundary>
      <TagContextProvider>
        <TagTableView
          currTags={value || []}
          tagsKind={kind}
          handleAdd={(tag) => handleAddTags(data, tag)}
          handleRemove={(id) => handleRemoveTag(data, id)}
          tableActions={tableActions}
          isCreateModal={isCreateModal}
          setIsCreateModal={setIsCreateModal}
          usePendingState
          savingTagIds={savingTagIds}
          fromTable
          popoverPosition={popoverPosition}
          popoverPanelClassName={popoverPanelClassName}
          readOnly={readOnly}
          showTags={showTags}
          triggerLabel={triggerLabel}
          removeConfirmationTitle={`Are you sure you want to remove this tag from the ${label || kind}?`}
        />
        {!readOnly && (
          <TagsReorder
            openReorderModal={!!openReorderModal}
            handleClose={() => setOpenReorderModal(false)}
            refId={data?.id}
            refTable={refTable}
            fromTable
            kind={kind}
          />
        )}
      </TagContextProvider>
    </ErrorBoundary>
  );
};

export default Tags;
